import { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/env';
import { buildCrmSwalBody, fireConfirmDialog } from '../utils/swalConfirm';
import { CwListSearch } from '../components/conversaciones/CwUiParts';
import AmvTablePager from '../components/admin/AmvTablePager';
import UiIcon from '../components/UiIcon';
import '../styles/conversaciones-premium.css';
import '../styles/gestion-derivacion-premium.css';
import '../styles/admin-medicos-visibilidad-premium.css';
import '../styles/farmacia-sucursales-admin.css';
import '../styles/farmacia-horarios-bot-admin.css';

const ENDPOINTS = {
  list: '/api/atencion-bot/horarios-botrecetas',
  create: '/api/atencion-bot/horarios-botrecetas',
  update: (id) => `/api/atencion-bot/horarios-botrecetas/${encodeURIComponent(id)}`,
  delete: (id) => `/api/atencion-bot/horarios-botrecetas/${encodeURIComponent(id)}`
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DAY_OPTIONS = [
  { key: 'lu', label: 'Lun' },
  { key: 'ma', label: 'Mar' },
  { key: 'mi', label: 'Mié' },
  { key: 'ju', label: 'Jue' },
  { key: 'vi', label: 'Vie' },
  { key: 'sa', label: 'Sáb' },
  { key: 'do', label: 'Dom' }
];
const DEFAULT_TIMEZONE = 'America/Argentina/Mendoza';
const TIMEZONE_OPTIONS = [
  { value: 'America/Argentina/Mendoza', label: 'Mendoza' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires' },
  { value: 'America/Argentina/Cordoba', label: 'Cordoba' },
  { value: 'America/Argentina/San_Luis', label: 'San Luis' },
  { value: 'America/Argentina/San_Juan', label: 'San Juan' },
  { value: 'America/Argentina/Tucuman', label: 'Tucuman' },
  { value: 'America/Argentina/Salta', label: 'Salta' },
  { value: 'America/Argentina/Jujuy', label: 'Jujuy' },
  { value: 'America/Argentina/Catamarca', label: 'Catamarca' },
  { value: 'America/Argentina/La_Rioja', label: 'La Rioja' },
  { value: 'America/Argentina/Rio_Gallegos', label: 'Rio Gallegos' },
  { value: 'America/Argentina/Ushuaia', label: 'Ushuaia' }
];

function showToast({ title, text, icon = 'success' }) {
  Swal.fire({
    toast: true,
    position: 'bottom-end',
    icon,
    title,
    text,
    showConfirmButton: false,
    timer: 3200,
    timerProgressBar: true,
    customClass: { popup: 'swal-toast-crm swal-toast-crm--amv' }
  });
}

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function createScheduleBlock() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    days: ['lu', 'ma', 'mi', 'ju', 'vi'],
    from: '09:00',
    to: '21:00',
    split: false,
    from2: '',
    to2: ''
  };
}

function normalizeTime(value, fallback = '09:00') {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return fallback;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return fallback;
  }
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function normalizeBlock(block) {
  const days = [...new Set((Array.isArray(block?.days) ? block.days : []).filter((item) => DAY_OPTIONS.some((day) => day.key === item)))];
  const splitFlag = Boolean(block?.split);
  const rawFrom2 = String(block?.from2 ?? block?.desde2 ?? '').trim();
  const rawTo2 = String(block?.to2 ?? block?.hasta2 ?? '').trim();
  const from2 = normalizeTime(rawFrom2, '17:00');
  const to2 = normalizeTime(rawTo2, '21:00');
  const hasSecondWindow = Boolean(rawFrom2 && rawTo2);
  return {
    id: String(block?.id || '').trim() || createScheduleBlock().id,
    days,
    from: normalizeTime(block?.from, '09:00'),
    to: normalizeTime(block?.to, '21:00'),
    split: splitFlag && hasSecondWindow,
    from2: splitFlag && hasSecondWindow ? from2 : '',
    to2: splitFlag && hasSecondWindow ? to2 : ''
  };
}

function normalizeBlocks(value) {
  const source = Array.isArray(value) ? value : [];
  const normalized = source.map(normalizeBlock).filter((block) => block.days.length && block.from && block.to);
  return normalized.length ? normalized : [createScheduleBlock()];
}

function serializeBlockForPayload(block) {
  const normalized = normalizeBlock(block);
  const split = Boolean(normalized.split && normalized.from2 && normalized.to2);
  return {
    id: normalized.id,
    days: normalized.days,
    from: normalized.from,
    to: normalized.to,
    split,
    from2: split ? normalized.from2 : null,
    to2: split ? normalized.to2 : null
  };
}

function serializeBlocksForPayload(value) {
  return normalizeBlocks(value).map(serializeBlockForPayload);
}

function sortRows(list) {
  return [...list].sort((a, b) => {
    const activeDelta = Number(b?.activo || 0) - Number(a?.activo || 0);
    if (activeDelta !== 0) return activeDelta;
    return String(a?.nombre || '').localeCompare(String(b?.nombre || ''), 'es', { sensitivity: 'base' });
  });
}

function formatDays(days) {
  const unique = [...new Set((Array.isArray(days) ? days : []).filter(Boolean))];
  const ordered = DAY_OPTIONS.filter((day) => unique.includes(day.key));
  if (!ordered.length) return 'Sin días';
  const values = ordered.map((item) => item.key);
  if (values.length === 7) return 'Lunes a Domingo';
  if (values.join(',') === 'lu,ma,mi,ju,vi') return 'Lunes a Viernes';
  if (values.join(',') === 'lu,ma,mi,ju,vi,sa') return 'Lunes a Sábados';
  return ordered.map((item) => item.label).join(', ');
}

function formatBlock(block) {
  const first = `${block.from} a ${block.to}`;
  const second = block.split && block.from2 && block.to2 ? ` y de ${block.from2} a ${block.to2}` : '';
  return `${formatDays(block.days)} de ${first}${second}`;
}

function formatSchedulePreview(blocks) {
  return normalizeBlocks(blocks)
    .filter((block) => block.days.length && block.from && block.to)
    .map(formatBlock)
    .join(' · ');
}

function countCoveredDays(blocks) {
  const days = new Set();
  normalizeBlocks(blocks).forEach((block) => block.days.forEach((day) => days.add(day)));
  return days.size;
}

function totalScheduleWindows(blocks) {
  return normalizeBlocks(blocks).reduce((acc, block) => acc + 1 + (block.split ? 1 : 0), 0);
}

function botStatusText(value) {
  return Number(value) === 0 ? 'Pausado' : 'Activo';
}

function emptyFormState() {
  return {
    nombre: '',
    timezone: DEFAULT_TIMEZONE,
    activo: 1,
    franjas_json: [createScheduleBlock()],
    mensaje_fuera_horario: '',
    notas: ''
  };
}

function formStateFromRow(row) {
  return {
    nombre: String(row?.nombre || ''),
    timezone: String(row?.timezone || DEFAULT_TIMEZONE),
    activo: Number(row?.activo) === 0 ? 0 : 1,
    franjas_json: normalizeBlocks(row?.franjas_json),
    mensaje_fuera_horario: String(row?.mensaje_fuera_horario || ''),
    notas: String(row?.notas || '')
  };
}

function payloadFromForm(form) {
  return {
    nombre: String(form?.nombre || '').trim(),
    timezone: String(form?.timezone || DEFAULT_TIMEZONE).trim() || DEFAULT_TIMEZONE,
    activo: Number(form?.activo) === 0 ? 0 : 1,
    franjas_json: serializeBlocksForPayload(form?.franjas_json),
    mensaje_fuera_horario: String(form?.mensaje_fuera_horario || '').trim(),
    notas: String(form?.notas || '').trim()
  };
}

function TimezoneSelect({ value, onChange }) {
  const selectedTimezone = String(value || DEFAULT_TIMEZONE).trim() || DEFAULT_TIMEZONE;
  const options = TIMEZONE_OPTIONS.some((option) => option.value === selectedTimezone)
    ? TIMEZONE_OPTIONS
    : [{ value: selectedTimezone, label: selectedTimezone }, ...TIMEZONE_OPTIONS];

  return (
    <select value={selectedTimezone} onChange={(event) => onChange(event.target.value)} aria-label="Timezone">
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label} - {option.value}
        </option>
      ))}
    </select>
  );
}

function BotStatusToggle({ value, onChange }) {
  const isActive = Number(value) !== 0;
  return (
    <button
      type="button"
      className={`farmacia-sucursales-admin__cadeteria-toggle farmacia-horarios-bot-admin__status-toggle${isActive ? ' is-on' : ''}`}
      onClick={() => onChange?.(isActive ? 0 : 1)}
      aria-pressed={isActive ? 'true' : 'false'}
    >
      <span className="farmacia-sucursales-admin__cadeteria-track">
        <span className="farmacia-sucursales-admin__cadeteria-thumb" />
      </span>
      <span className="farmacia-sucursales-admin__cadeteria-copy farmacia-horarios-bot-admin__status-copy">
        <strong>{isActive ? 'Chat habilitado' : 'Chat pausado'}</strong>
      </span>
    </button>
  );
}

function ScheduleComposer({ value, onChange, accent = 'amber', onRemoveBlock }) {
  const blocks = normalizeBlocks(value);

  function commit(nextBlocks) {
    onChange?.(normalizeBlocks(nextBlocks));
  }

  function updateBlock(blockId, patch) {
    commit(blocks.map((block) => (block.id === blockId ? { ...block, ...patch } : block)));
  }

  function toggleDay(blockId, dayKey) {
    commit(
      blocks.map((block) => {
        if (block.id !== blockId) return block;
        const exists = block.days.includes(dayKey);
        return {
          ...block,
          days: exists ? block.days.filter((key) => key !== dayKey) : [...block.days, dayKey]
        };
      })
    );
  }

  function addBlock() {
    commit([...blocks, createScheduleBlock()]);
  }

  function removeBlock(blockId) {
    if (onRemoveBlock) {
      onRemoveBlock(blockId, blocks);
      return;
    }
    if (blocks.length <= 1) {
      commit([createScheduleBlock()]);
      return;
    }
    commit(blocks.filter((block) => block.id !== blockId));
  }

  const preview = formatSchedulePreview(blocks) || 'Todavía no definiste una franja operativa.';

  return (
    <div className={`farmacia-horarios-bot-admin__schedule-card is-${accent}`}>
      <div className="farmacia-horarios-bot-admin__schedule-head">
        <div>
          <strong>Agenda operativa</strong>
        </div>
        <button type="button" className="farmacia-sucursales-admin__ghost" onClick={addBlock}>Agregar</button>
      </div>

      <div className="farmacia-horarios-bot-admin__schedule-preview">
        <strong>{preview}</strong>
      </div>

      <div className="farmacia-horarios-bot-admin__schedule-grid">
        {blocks.map((block, index) => (
          <article key={block.id} className="farmacia-horarios-bot-admin__schedule-block">
            <div className="farmacia-horarios-bot-admin__schedule-block-head">
              <div>
                <strong>Franja {index + 1}</strong>
                <small>{formatDays(block.days)}</small>
              </div>
              <button type="button" className="farmacia-horarios-bot-admin__schedule-remove" onClick={() => removeBlock(block.id)}>
                Quitar
              </button>
            </div>

            <div className="farmacia-horarios-bot-admin__day-list">
              {DAY_OPTIONS.map((day) => {
                const active = block.days.includes(day.key);
                return (
                  <button
                    key={day.key}
                    type="button"
                    className={`farmacia-horarios-bot-admin__day-pill${active ? ' is-active' : ''}`}
                    onClick={() => toggleDay(block.id, day.key)}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>

            <div className="farmacia-horarios-bot-admin__time-grid">
              <label>
                <span>Desde</span>
                <input type="time" value={block.from} onChange={(e) => updateBlock(block.id, { from: e.target.value })} />
              </label>
              <label>
                <span>Hasta</span>
                <input type="time" value={block.to} onChange={(e) => updateBlock(block.id, { to: e.target.value })} />
              </label>
              <button
                type="button"
                className={`farmacia-horarios-bot-admin__split-toggle${block.split ? ' is-active' : ''}`}
                onClick={() => updateBlock(block.id, block.split
                  ? { split: false, from2: '', to2: '' }
                  : {
                      split: true,
                      from2: block.from2 || '17:00',
                      to2: block.to2 || '21:00'
                    })}
              >
                {block.split ? 'Quitar corte' : 'Agregar corte'}
              </button>
            </div>

            {block.split ? (
              <div className="farmacia-horarios-bot-admin__time-grid farmacia-horarios-bot-admin__time-grid--split">
                <label>
                  <span>Desde 2</span>
                  <input type="time" value={block.from2} onChange={(e) => updateBlock(block.id, { from2: e.target.value })} />
                </label>
                <label>
                  <span>Hasta 2</span>
                  <input type="time" value={block.to2} onChange={(e) => updateBlock(block.id, { to2: e.target.value })} />
                </label>
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </div>
  );
}

export default function FarmaciaHorariosBotAdmin() {
  const [horarios, setHorarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedId, setSelectedId] = useState('');
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyFormState());
  const [creating, setCreating] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(emptyFormState());
  const [editSaving, setEditSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.list}`, { credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const next = sortRows(Array.isArray(json?.horarios) ? json.horarios : []);
      setHorarios(next);
      setSelectedId((current) => current || String(next[0]?.id || ''));
    } catch (err) {
      setError(err.message || 'No se pudo cargar la agenda operativa del bot.');
      setHorarios([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!createDrawerOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !creating) {
        setCreateDrawerOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [createDrawerOpen, creating]);

  useEffect(() => {
    if (!editRow) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !editSaving) {
        setEditRow(null);
        setEditForm(emptyFormState());
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editRow, editSaving]);

  const filtered = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return horarios;
    return horarios.filter((item) => {
      const bag = [
        item?.nombre,
        item?.timezone,
        item?.mensaje_fuera_horario,
        item?.notas,
        botStatusText(item?.activo),
        formatSchedulePreview(item?.franjas_json)
      ];
      return bag.some((value) => normalizeText(value).includes(q));
    });
  }, [horarios, search]);

  const totalPages = useMemo(() => {
    if (pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(filtered.length / pageSize));
  }, [filtered.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const pageItems = useMemo(() => {
    if (pageSize <= 0) return filtered;
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const selectedHorario = useMemo(
    () => horarios.find((item) => String(item?.id) === String(selectedId || '')) || filtered[0] || null,
    [filtered, horarios, selectedId]
  );

  useEffect(() => {
    if (!selectedHorario && horarios[0]) {
      setSelectedId(String(horarios[0].id));
    }
  }, [horarios, selectedHorario]);

  const activeCount = useMemo(() => horarios.filter((item) => Number(item?.activo) !== 0).length, [horarios]);
  const totalBlocks = useMemo(() => horarios.reduce((acc, item) => acc + normalizeBlocks(item?.franjas_json).length, 0), [horarios]);
  const totalWindows = useMemo(() => horarios.reduce((acc, item) => acc + totalScheduleWindows(item?.franjas_json), 0), [horarios]);
  const activeRow = useMemo(() => horarios.find((item) => Number(item?.activo) !== 0) || null, [horarios]);
  const selectedCoverageDays = useMemo(() => countCoveredDays(selectedHorario?.franjas_json), [selectedHorario]);

  const rangeStart = filtered.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, filtered.length);

  function updateCreateField(field, value) {
    setCreateForm((current) => ({ ...current, [field]: value }));
  }

  function updateEditField(field, value) {
    setEditForm((current) => ({ ...current, [field]: value }));
  }

  function openEdit(row) {
    setEditRow(row);
    setEditForm(formStateFromRow(row));
  }

  function closeCreateDrawer() {
    if (creating) return;
    setCreateDrawerOpen(false);
    setCreateForm(emptyFormState());
  }

  function closeEdit() {
    if (editSaving) return;
    setEditRow(null);
    setEditForm(emptyFormState());
  }

  async function handleCreate(event) {
    event.preventDefault();
    const payload = payloadFromForm(createForm);
    if (!payload.nombre || !payload.franjas_json.length) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.create}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const created = json?.horario;
      const next = sortRows(created ? [...horarios, created] : horarios);
      setHorarios(next);
      setSelectedId(String(created?.id || selectedId || ''));
      setCreateDrawerOpen(false);
      setCreateForm(emptyFormState());
      showToast({ title: 'Agenda creada', text: created?.nombre || 'La configuración quedó disponible.' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo crear', text: err.message || 'Error al crear la agenda del bot.' });
    } finally {
      setCreating(false);
    }
  }

  async function handleEdit(event) {
    event.preventDefault();
    if (!editRow || editSaving) return;
    const payload = payloadFromForm(editForm);
    if (!payload.nombre || !payload.franjas_json.length) return;
    setEditSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.update(editRow.id)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const updated = json?.horario;
      const next = sortRows(horarios.map((item) => (Number(item?.id) === Number(editRow?.id) ? updated : item)));
      setHorarios(next);
      setSelectedId(String(updated?.id || editRow?.id || ''));
      closeEdit();
      showToast({ title: 'Agenda actualizada', text: updated?.nombre || 'Se guardaron los cambios.' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo actualizar', text: err.message || 'Error al actualizar la agenda.' });
    } finally {
      setEditSaving(false);
    }
  }

  async function requestDelete(row) {
    const confirm = await fireConfirmDialog({
      title: 'Eliminar configuración horaria',
      html: buildCrmSwalBody({
        lead: 'Se va a quitar esta agenda del tablero operativo de BotRecetas.',
        details: [
          { label: 'Nombre', value: row?.nombre || 'Sin nombre' },
          { label: 'Estado', value: botStatusText(row?.activo) }
        ],
        items: [
          'Si era la agenda activa, el bot quedará sin una configuración principal hasta que actives otra',
          'La definición de franjas se elimina definitivamente'
        ]
      }),
      icon: 'warning',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      width: 'min(440px, 92vw)'
    });
    if (!confirm.isConfirmed) return;

    setDeletingId(String(row?.id || ''));
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.delete(row.id)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const next = horarios.filter((item) => Number(item?.id) !== Number(row?.id));
      setHorarios(next);
      if (String(selectedId) === String(row?.id)) {
        setSelectedId(String(next[0]?.id || ''));
      }
      showToast({ title: 'Agenda eliminada', text: row?.nombre || 'La configuración se eliminó correctamente.' });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo eliminar', text: err.message || 'Error al eliminar la agenda.' });
    } finally {
      setDeletingId('');
    }
  }

  async function requestRemoveBlock(blockId, currentBlocks, onChange) {
    if (!blockId) return;
    const confirm = await fireConfirmDialog({
      title: 'Quitar franja horaria',
      html: buildCrmSwalBody({
        lead: 'Vas a eliminar una franja de esta agenda.',
        details: [
          { label: 'Franjas actuales', value: String(Array.isArray(currentBlocks) ? currentBlocks.length : 0) }
        ],
        items: [
          'La franja se quitará del borrador o de la agenda actual',
          'Si era la única franja, la pantalla volverá a crear una base mínima para que no quede vacía'
        ]
      }),
      icon: 'warning',
      confirmButtonText: 'Quitar franja',
      cancelButtonText: 'Cancelar',
      width: 'min(430px, 92vw)'
    });
    if (!confirm.isConfirmed) return;

    const nextBlocks = (Array.isArray(currentBlocks) && currentBlocks.length > 1)
      ? currentBlocks.filter((block) => String(block?.id || '') !== String(blockId))
      : [createScheduleBlock()];
    onChange?.(nextBlocks);
  }

  return (
    <section className="farmacia-sucursales-admin farmacia-horarios-bot-admin crm-gd-page crm-pl-page crm-pbody">
      <div className="farmacia-sucursales-admin__shell">
        <div className="farmacia-sucursales-admin__top">
          <header className="farmacia-sucursales-admin__hero farmacia-horarios-bot-admin__hero">
            <div>
              <span className="farmacia-sucursales-admin__eyebrow">FarmaChat · Horarios del Bot</span>
            </div>
            {/* <button type="button" className="farmacia-sucursales-admin__primary farmacia-sucursales-admin__hero-action" onClick={() => setCreateDrawerOpen(true)}>
              <UiIcon name="plus" />
              <span>Nueva agenda</span>
            </button> */}
          </header>

          <div className="farmacia-sucursales-admin__stats farmacia-horarios-bot-admin__stats-grid">
            <article>
              <span>Configuraciones</span>
              <strong>{horarios.length}</strong>
              <small>Perfiles horarios cargados para BotRecetas</small>
            </article>
            <article>
              <span>Activas</span>
              <strong>{activeCount}</strong>
              <small>Siempre debe quedar como máximo una</small>
            </article>
            <article>
              <span>Franjas</span>
              <strong>{totalBlocks}</strong>
              <small>{totalWindows} ventanas contando cortes</small>
            </article>
            <article>
              <span>Agenda líder</span>
              <strong>{activeRow?.nombre || 'Sin agenda activa'}</strong>
              <small>{activeRow ? formatSchedulePreview(activeRow?.franjas_json) : 'Activá una configuración para fijar el horario principal del bot.'}</small>
            </article>
          </div>
        </div>

        <div className="farmacia-sucursales-admin__scroll">
          <div className="farmacia-sucursales-admin__grid farmacia-horarios-bot-admin__grid">
            <div className="farmacia-sucursales-admin__main">
              <div className="farmacia-sucursales-admin__panel">
                <div className="farmacia-sucursales-admin__toolbar">
                  <div>
                    <h2>Agendas disponibles</h2>
                    <p>Buscá por nombre, timezone, mensaje de cierre o por el resumen de franjas.</p>
                  </div>
                  <div className="farmacia-sucursales-admin__toolbar-actions">
                    <div className="farmacia-sucursales-admin__search-wrap">
                      <CwListSearch value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar agenda…" />
                    </div>
                    <label className="farmacia-sucursales-admin__page-size">
                      <span>Ver</span>
                      <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value) || 10)}>
                        {PAGE_SIZE_OPTIONS.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>

                {error ? (
                  <div className="farmacia-sucursales-admin__error">
                    <UiIcon name="alert" />
                    <span>{error}</span>
                  </div>
                ) : null}

                <div className="farmacia-sucursales-admin__table-wrap">
                  <table className="farmacia-sucursales-admin__table farmacia-horarios-bot-admin__table">
                    <thead>
                      <tr>
                        <th>Agenda</th>
                        <th>Franja</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={4} className="farmacia-sucursales-admin__empty">
                            <div className="farmacia-sucursales-admin__loading-state farmacia-horarios-bot-admin__loading-state" role="status" aria-live="polite">
                              <div className="farmacia-sucursales-admin__loading-badge">
                                <span className="farmacia-sucursales-admin__loading-spinner" aria-hidden="true" />
                                <strong>Sincronizando agenda del bot</strong>
                              </div>
                              <span>Estamos recuperando ventanas, días y estado operativo del chat.</span>
                              <div className="farmacia-sucursales-admin__loading-skeleton" aria-hidden="true">
                                <span />
                                <span />
                                <span />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : pageItems.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="farmacia-sucursales-admin__empty">
                            <div className="farmacia-sucursales-admin__empty-state" role="status" aria-live="polite">
                              <div className="farmacia-sucursales-admin__empty-icon" aria-hidden="true">
                                <span className="farmacia-sucursales-admin__empty-icon-ring" />
                                <UiIcon name="search" />
                              </div>
                              <div className="farmacia-sucursales-admin__empty-copy">
                                <strong>No hay agendas para mostrar</strong>
                                <span>{String(search || '').trim() ? 'Probá con otro término para encontrar la configuración horaria.' : 'Creá la primera agenda operativa para controlar cuándo vive el chat.'}</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : pageItems.map((row) => {
                        const isSelected = String(selectedId) === String(row?.id);
                        return (
                          <tr key={row.id} className={isSelected ? 'is-selected' : ''} onClick={() => setSelectedId(String(row.id))} aria-selected={isSelected ? 'true' : 'false'}>
                            <td>
                              <div className="farmacia-sucursales-admin__branch-main">
                                <div className="farmacia-sucursales-admin__branch-title-row">
                                  <strong>{row.nombre}</strong>
                                  {Number(row?.activo) !== 0 ? <span className="farmacia-horarios-bot-admin__active-dot" aria-hidden="true" /> : null}
                                </div>
                                <span className="farmacia-sucursales-admin__branch-subtitle farmacia-horarios-bot-admin__branch-subtitle">
                                  {row.timezone || DEFAULT_TIMEZONE} · {countCoveredDays(row?.franjas_json)} días cubiertos
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="farmacia-horarios-bot-admin__coverage-cell">
                                <span className="farmacia-sucursales-admin__geo-badge is-ready">{normalizeBlocks(row?.franjas_json).length} franjas</span>
                                <small>{formatSchedulePreview(row?.franjas_json)}</small>
                              </div>
                            </td>
                            <td>
                              <div className="farmacia-sucursales-admin__geo-cell">
                                <span className={`farmacia-sucursales-admin__geo-badge${Number(row?.activo) !== 0 ? ' is-ready' : ''}`}>{botStatusText(row?.activo)}</span>
                                <small>{Number(row?.activo) !== 0 ? 'Es la agenda vigente del chat' : 'Guardada sin exposición al bot'}</small>
                              </div>
                            </td>
                            <td>
                              <div className="farmacia-sucursales-admin__actions">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openEdit(row);
                                  }}
                                  aria-label={`Editar ${row.nombre}`}
                                >
                                  <UiIcon name="edit" />
                                </button>
                                {/* <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void requestDelete(row);
                                  }}
                                  disabled={deletingId === String(row.id)}
                                  aria-label={`Eliminar ${row.nombre}`}
                                >
                                  <UiIcon name="trash" />
                                </button> */}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <AmvTablePager
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  filteredCount={filtered.length}
                  totalCount={horarios.length}
                  searchActive={Boolean(String(search || '').trim())}
                  page={page}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  entityLabel="agendas"
                />
              </div>
            </div>

            <aside className="farmacia-sucursales-admin__side farmacia-horarios-bot-admin__side">
              <div className="farmacia-sucursales-admin__panel farmacia-horarios-bot-admin__spotlight">
                <div className="farmacia-sucursales-admin__panel-head farmacia-sucursales-admin__panel-head--side">
                  <div>
                    <span className="farmacia-horarios-bot-admin__panel-kicker">Ventana actual</span>
                    <h3>{selectedHorario?.nombre || 'Sin selección'}</h3>
                    <p>{selectedHorario ? 'Resumen premium de la agenda que quedaría expuesta al bot.' : 'Elegí una agenda del listado para revisar timezone, franjas y mensaje de cierre.'}</p>
                  </div>
                </div>

                {selectedHorario ? (
                  <div className="farmacia-horarios-bot-admin__detail-card">
                    <div className="farmacia-horarios-bot-admin__detail-head">
                      <div>
                        <span>{botStatusText(selectedHorario?.activo)}</span>
                        <strong>{selectedHorario?.nombre}</strong>
                      </div>
                      <span className={`farmacia-horarios-bot-admin__status-pill${Number(selectedHorario?.activo) !== 0 ? ' is-live' : ''}`}>{selectedHorario?.timezone || DEFAULT_TIMEZONE}</span>
                    </div>

                    <div className="farmacia-horarios-bot-admin__detail-highlights">
                      <article>
                        <span>Días cubiertos</span>
                        <strong>{selectedCoverageDays}</strong>
                        <small>{selectedCoverageDays === 7 ? 'La agenda cubre toda la semana.' : 'Cantidad de días en los que el chat queda habilitado.'}</small>
                      </article>
                      <article>
                        <span>Ventanas</span>
                        <strong>{totalScheduleWindows(selectedHorario?.franjas_json)}</strong>
                        <small>Cuenta cada tramo y cada corte horario.</small>
                      </article>
                    </div>

                    <div className="farmacia-horarios-bot-admin__preview-shell">
                      <span>Vista textual</span>
                      <strong>{formatSchedulePreview(selectedHorario?.franjas_json)}</strong>
                    </div>

                    <div className="farmacia-horarios-bot-admin__block-list">
                      {normalizeBlocks(selectedHorario?.franjas_json).map((block) => (
                        <article key={block.id} className="farmacia-horarios-bot-admin__block-chip">
                          <span>{formatDays(block.days)}</span>
                          <strong>{block.from} - {block.to}</strong>
                          {block.split ? <small>{block.from2} - {block.to2}</small> : <small>Sin corte</small>}
                        </article>
                      ))}
                    </div>

                    <dl className="farmacia-horarios-bot-admin__detail-list">
                      <div><dt>Timezone</dt><dd>{selectedHorario?.timezone || DEFAULT_TIMEZONE}</dd></div>
                      <div><dt>Estado</dt><dd>{botStatusText(selectedHorario?.activo)}</dd></div>
                      <div className="is-wide"><dt>Mensaje fuera de horario</dt><dd>{selectedHorario?.mensaje_fuera_horario || 'Sin mensaje configurado'}</dd></div>
                      <div className="is-wide"><dt>Notas</dt><dd>{selectedHorario?.notas || 'Sin notas internas'}</dd></div>
                    </dl>
                  </div>
                ) : (
                  <div className="farmacia-sucursales-admin__empty-card farmacia-horarios-bot-admin__empty-card">
                    No hay una agenda seleccionada. Cuando cargues o elijas una, acá vas a ver el resumen operativo completo.
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </div>

      <div
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--left farmacia-sucursales-admin__drawer-backdrop${createDrawerOpen ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'}`}
        role="presentation"
        onClick={closeCreateDrawer}
        aria-hidden={!createDrawerOpen}
      >
        <aside
          className={`crm-gd-drawer-panel farmacia-sucursales-admin__drawer farmacia-horarios-bot-admin__drawer${createDrawerOpen ? ' crm-gd-drawer-panel--left-open' : ' crm-gd-drawer-panel--left-closed'}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="farmacia-horarios-bot-create-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="farmacia-sucursales-admin__drawer-stripe farmacia-horarios-bot-admin__drawer-stripe" aria-hidden="true" />
          <div className="farmacia-sucursales-admin__drawer-head">
            <div>
              <span className="farmacia-sucursales-admin__drawer-eyebrow">Nueva agenda</span>
              <h2 id="farmacia-horarios-bot-create-title">Agregar horario activo</h2>
              <p>Definí una ventana elegante y exacta para el chat. Si la activás, reemplaza automáticamente cualquier otra agenda viva.</p>
            </div>
            <button type="button" className="farmacia-sucursales-admin__drawer-close" onClick={closeCreateDrawer} aria-label="Cerrar drawer">×</button>
          </div>

          <form className="farmacia-sucursales-admin__drawer-form" onSubmit={handleCreate}>
            <div className="farmacia-sucursales-admin__drawer-body farmacia-horarios-bot-admin__drawer-body">
              <section className="farmacia-horarios-bot-admin__form-cluster">
                <label className="farmacia-horarios-bot-admin__compact-field farmacia-horarios-bot-admin__compact-field--name">
                  <span>Nombre</span>
                  <input value={createForm.nombre} onChange={(e) => updateCreateField('nombre', e.target.value)} placeholder="Horario comercial principal" required />
                </label>

                <div className="farmacia-horarios-bot-admin__meta-grid">
                  <label className="farmacia-horarios-bot-admin__meta-card">
                    <span>Timezone</span>
                    <TimezoneSelect value={createForm.timezone} onChange={(value) => updateCreateField('timezone', value)} />
                  </label>
                  {/* <div className="farmacia-horarios-bot-admin__meta-card farmacia-horarios-bot-admin__meta-card--status">
                    <span>Estado operativo</span>
                    <BotStatusToggle value={createForm.activo} onChange={(value) => updateCreateField('activo', value)} />
                  </div> */}
                </div>
              </section>

              <ScheduleComposer
                value={createForm.franjas_json}
                onChange={(value) => updateCreateField('franjas_json', value)}
                onRemoveBlock={(blockId, currentBlocks) => requestRemoveBlock(blockId, currentBlocks, (nextBlocks) => updateCreateField('franjas_json', nextBlocks))}
                accent="amber"
              />

              <div className="farmacia-horarios-bot-admin__text-grid">
                <label className="is-wide">
                  <span>Mensaje fuera de horario</span>
                  <textarea value={createForm.mensaje_fuera_horario} onChange={(e) => updateCreateField('mensaje_fuera_horario', e.target.value)} placeholder="Hola, ahora estamos fuera de horario. Dejame tu pedido y te respondemos apenas retomemos la atención." rows={4} />
                </label>
              </div>
            </div>

            <div className="farmacia-sucursales-admin__drawer-actions farmacia-horarios-bot-admin__drawer-actions">
              <button type="button" className="farmacia-sucursales-admin__ghost" onClick={closeCreateDrawer} disabled={creating}>Cancelar</button>
              <button type="submit" className="farmacia-sucursales-admin__primary" disabled={creating || !String(createForm.nombre || '').trim()}>
                {creating ? 'Guardando…' : 'Guardar agenda'}
              </button>
            </div>
          </form>
        </aside>
      </div>

      <div
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--left farmacia-sucursales-admin__drawer-backdrop${editRow ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'}`}
        role="presentation"
        onClick={closeEdit}
        aria-hidden={!editRow}
      >
        <aside
          className={`crm-gd-drawer-panel farmacia-sucursales-admin__drawer farmacia-sucursales-admin__drawer--edit farmacia-horarios-bot-admin__drawer${editRow ? ' crm-gd-drawer-panel--left-open' : ' crm-gd-drawer-panel--left-closed'}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="farmacia-horarios-bot-edit-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="farmacia-sucursales-admin__drawer-stripe farmacia-sucursales-admin__drawer-stripe--edit farmacia-horarios-bot-admin__drawer-stripe" aria-hidden="true" />
          <div className="farmacia-sucursales-admin__drawer-head">
            <div>
              <span className="farmacia-sucursales-admin__drawer-eyebrow">Editar agenda</span>
            </div>
            <button type="button" className="farmacia-sucursales-admin__drawer-close" onClick={closeEdit} aria-label="Cerrar drawer">×</button>
          </div>

          <form className="farmacia-sucursales-admin__drawer-form" onSubmit={handleEdit}>
            <div className="farmacia-sucursales-admin__drawer-body farmacia-horarios-bot-admin__drawer-body">
              <section className="farmacia-horarios-bot-admin__form-cluster">
                <label className="farmacia-horarios-bot-admin__compact-field farmacia-horarios-bot-admin__compact-field--name">
                  <span>Nombre</span>
                  <input value={editForm.nombre} onChange={(e) => updateEditField('nombre', e.target.value)} placeholder="Horario comercial principal" required />
                </label>

                <div className="farmacia-horarios-bot-admin__meta-grid">
                  <label className="farmacia-horarios-bot-admin__meta-card">
                    <span>Timezone</span>
                    <TimezoneSelect value={editForm.timezone} onChange={(value) => updateEditField('timezone', value)} />
                  </label>
                  {/* <div className="farmacia-horarios-bot-admin__meta-card farmacia-horarios-bot-admin__meta-card--status">
                    <span>Estado operativo</span>
                    <BotStatusToggle value={editForm.activo} onChange={(value) => updateEditField('activo', value)} />
                  </div> */}
                </div>
              </section>

              <ScheduleComposer
                value={editForm.franjas_json}
                onChange={(value) => updateEditField('franjas_json', value)}
                onRemoveBlock={(blockId, currentBlocks) => requestRemoveBlock(blockId, currentBlocks, (nextBlocks) => updateEditField('franjas_json', nextBlocks))}
                accent="blue"
              />

              <div className="farmacia-horarios-bot-admin__text-grid">
                <label className="is-wide">
                  <span>Mensaje fuera de horario</span>
                  <textarea value={editForm.mensaje_fuera_horario} onChange={(e) => updateEditField('mensaje_fuera_horario', e.target.value)} placeholder="Mensaje automático fuera de horario" rows={4} />
                </label>
                {/* <label className="is-wide">
                  <span>Notas internas</span>
                  <textarea value={editForm.notas} onChange={(e) => updateEditField('notas', e.target.value)} placeholder="Observaciones internas" rows={3} />
                </label> */}
              </div>
            </div>

            <div className="farmacia-sucursales-admin__drawer-actions farmacia-horarios-bot-admin__drawer-actions">
              <button type="button" className="farmacia-sucursales-admin__ghost" onClick={closeEdit} disabled={editSaving}>Cancelar</button>
              <button type="submit" className="farmacia-sucursales-admin__primary" disabled={editSaving || !String(editForm.nombre || '').trim()}>
                {editSaving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        </aside>
      </div>
    </section>
  );
}
